import React, { useMemo } from 'react';

import useClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useClaimFlagConfigurations';
import type { ClaimFlagConfiguration } from '~/components/ClaimFlags/types';
import Chip from '~/components/core/Atomic/Chip/Chip';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import { colorsToBgBorderAndTextMap } from '~/components/core/Molecules/EditableChip';
import { OverflowList } from '~/components/core/Molecules/OverflowList';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import Popover from '~/components/ui/atoms/Popover';
import usePopoverState from '~/components/ui/atoms/Popover/usePopoverState';
import { prettifyKey } from '~/Utils';
import cn from '~/Utils/cn';

// Truncate long labels inside the bounded claim-bar column (the picker menu is unbounded and caps nothing).
const FLAG_LABEL_MAX_WIDTH = '120px';

/** Why a chip is neutral rather than its own colour. Narrower than "no longer configured", which would
 *  also describe an orphan — a different state, already rendered differently. */
export const DISABLED_FLAG_TOOLTIP = "This flag's configuration is disabled. It can be removed, but not re-added.";

const OVERFLOW_POPOVER_LABEL = 'Claim flags';

// An applied flag whose configuration was since disabled reads as *not operational* at a glance instead
// of as an active flag. A fixed neutral rather than a muted version of the flag's own colour: dropping
// the tint leaves the warm end of the palette around 2.4:1 against white, under WCAG 1.4.11's 3:1 for
// non-text, while the neutral is colour-independent and measures 11.6:1 (verified in Chrome, not jsdom).
// It also matches what an orphan already falls back to, so the two degraded states look the same.
const DISABLED_CONFIG_CLASS = 'border-grey-600 bg-slate-100 text-grey-600';

type FlagTag = { key: string; config?: ClaimFlagConfiguration };

type ClaimFlagChipsProps = {
  /** Applied flag keys. */
  flagKeys: string[];
  /** Provide to make each chip removable; omit for a read-only list. */
  onRemove?: (flagKey: string) => void;
  disabled?: boolean;
  /** Set a max-width here so the one-line list collapses in a bounded column. */
  className?: string;
};

const configuredChipClass = (config: ClaimFlagConfiguration): string =>
  config.is_enabled ? colorsToBgBorderAndTextMap[config.color] : DISABLED_CONFIG_CLASS;

const renderTagChip = (
  { key, config }: FlagTag,
  { onRemove, disabled }: Pick<ClaimFlagChipsProps, 'onRemove' | 'disabled'>
): React.ReactNode => {
  const chip = (
    <Chip
      size="small"
      // Configured flags are tinted in their colour (reused stage palette) unless their configuration
      // is disabled, in which case they go neutral; orphans show a prettified key in an outlined chip.
      // m-0 cancels the Atomic Chip wrapper's 5px margin so the row aligns with the stage pill.
      variant={config ? undefined : 'outlined'}
      className={cn('m-0 border-1 border-solid', config ? configuredChipClass(config) : '')}
      label={
        <OverflowTextWithToolTip maxWidth={FLAG_LABEL_MAX_WIDTH}>
          {config ? config.label : prettifyKey(key)}
        </OverflowTextWithToolTip>
      }
      disabled={disabled}
      // Removable whichever state it is in — being able to clear it is the reason it is shown at all.
      onDelete={onRemove ? () => onRemove(key) : undefined}
    />
  );

  // Only the disabled case is wrapped: an extra span around every chip would change what OverflowList
  // measures for the common path, and the other states need no explanation.
  return config && !config.is_enabled ? (
    <FsTooltip key={key} arrow title={DISABLED_FLAG_TOOLTIP}>
      {chip}
    </FsTooltip>
  ) : (
    React.cloneElement(chip, { key })
  );
};

/** The "+N" overflow chip: clicking it opens a popover listing every applied flag (interactive, so a
 *  chip hidden by the collapse can still be removed from here). */
const FlagOverflowChip = ({
  allTags,
  overflowCount,
  onRemove,
  disabled,
}: {
  allTags: FlagTag[];
  overflowCount: number;
  onRemove?: (flagKey: string) => void;
  disabled?: boolean;
}) => {
  const { triggerProps, popoverProps } = usePopoverState();
  return (
    <>
      <Chip size="small" clickable disabled={disabled} className="m-0" label={`+${overflowCount}`} {...triggerProps} />
      <Popover {...popoverProps} aria-label={OVERFLOW_POPOVER_LABEL} align="start">
        <div className="flex max-w-[320px] flex-wrap items-center gap-8 p-12">
          {allTags.map((tag) => renderTagChip(tag, { onRemove, disabled }))}
        </div>
      </Popover>
    </>
  );
};

/**
 * Reusable one-line flag-chip list for bounded surfaces such as the claim bar. Renders as many flag
 * chips as fit, then collapses the rest into a "+N" chip whose popover reveals the full set. Set a
 * max-width via `className` so it can collapse.
 *
 * Applied flags render in the admin config order (the API returns configs `(position, key)`-sorted);
 * orphans (applied but no config) fall to the end. What shows is decided by applied-ness alone: a flag
 * whose configuration was since disabled still renders, and stays removable, because the association
 * outlives the configuration being disabled and this list is the only place it is visible. Applying is
 * the asymmetric half — the picker offers enabled configurations only, so a disabled flag can be seen
 * and cleared here but not newly applied.
 */
const ClaimFlagChips = ({ flagKeys, onRemove, disabled, className }: ClaimFlagChipsProps) => {
  const { claimFlagConfigurations } = useClaimFlagConfigurations();

  const tags = useMemo<FlagTag[]>(() => {
    const applied = new Set(flagKeys);
    const configuredKeys = new Set(claimFlagConfigurations.map((config) => config.key));
    // Applied configs in the API's (position, key) order; orphans (applied but no config) last.
    const configured = claimFlagConfigurations
      .filter((config) => applied.has(config.key))
      .map((config) => ({ key: config.key, config }));
    const orphans = flagKeys.filter((key) => !configuredKeys.has(key)).map((key) => ({ key }));
    return [...configured, ...orphans];
  }, [flagKeys, claimFlagConfigurations]);

  if (tags.length === 0) {
    return null;
  }

  return (
    <OverflowList
      items={tags}
      minVisibleItems={1}
      className={cn('items-center gap-8', className)}
      itemRenderer={(tag) => renderTagChip(tag, { onRemove, disabled })}
      overflowRenderer={(overflow) => (
        <FlagOverflowChip allTags={tags} overflowCount={overflow.length} onRemove={onRemove} disabled={disabled} />
      )}
    />
  );
};

export default ClaimFlagChips;
